import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import memoize from 'lodash.memoize';
import { Grid } from '@mui/material';
import { Autocomplete, Typography } from '@totalsoft/rocket-ui';
import { withRights } from 'providers/RightsConsumer';
import { withApplicationFeatures } from 'providers/ApplicationFeaturesConsumer';
import { loadLegalForms, loadGenderTypes } from 'actions/actionCreators/dictionariesActions';
import {
  loadClientByIdCard,
  loadPartnerTypes,
  loadPartnerRoles,
  loadIdTypes,
  loadCitizenships,
  loadRelations,
  loadDefaultOCRFileExtensions,
  loadGDPR,
  loadPFPartners,
  loadLegalPartners,
  loadPartnerFromQuery,
  loadCities as loadCitiesWithCounty
} from '../actionCreators';
import { loadCountries, loadCounties, loadCities } from 'actions/actionCreators/dictionariesActions';
import OfferPartnerPFGeneralData from './OfferPartnerPFGeneralData';
import OfferPartnerPJGeneralData from './OfferPartnerPJGeneralData';
import { emptyString } from 'constants/emptyConst';
import { getOfferType, getDocumentType, getExternalVerificationStatus } from '../selectors';
import { getFilteredPartnerTypes } from '../../../assets/client/selectors';
import OfferType from 'constants/offerType';
import ExternalSearchConfirmationModal from './ExternalSearchConfirmationModal';
import documentTypeIds from 'constants/documentTypeIds';

class OfferPartnerGeneralDataContainer extends PureComponent {
  state = {
    showExternalSearchConfirmationModal: false
  };

  fileInputRef = React.createRef();

  toggleExternalSearchConfirmationModal = () =>
    this.setState(prevState => ({
      ...prevState,
      showExternalSearchConfirmationModal: !prevState.showExternalSearchConfirmationModal
    }));

  componentDidMount() {
    const { actions, isAssociatedPartner, applicationFeatures } = this.props;
    const { enableOcrIntegration } = applicationFeatures;
    actions.loadCitizenships();
    actions.loadRelations();
    actions.loadPartnerTypes();
    actions.loadLegalForms();
    actions.loadIdTypes();
    actions.loadGenderTypes();

    if (enableOcrIntegration) actions.loadDefaultOCRFileExtensions();

    if (isAssociatedPartner) {
      actions.loadPartnerRoles(this.props.documentTypeId);
    }
  }

  loadCitizenships = memoize((input = null) => {
    return this.props.actions.loadCitizenships(input).then(data => {
      return { options: data };
    });
  });

  loadRelations = memoize((input = null) => {
    return this.props.actions.loadRelations(input).then(data => {
      return { options: data };
    });
  });

  handleUpload = memoize(async file => {
    this.props.actions.loadClientByIdCard(file, this.props.partnerId, this.props.documentId, this.props.siteId);
  });

  uploadClick = () => {
    this.fileInputRef.current.value = emptyString;
    this.fileInputRef.current.click();
  };

  onFileSelected = event => {
    if (event.target.files.length > 0) {
      let file = event.target.files[0];
      this.handleUpload(file);
    }
  };

  getCompanyInfo = () => {
    const { handleExternalSearch } = this.props;
    handleExternalSearch(() => {
      const { canSearchExternally } = this.props;
      if (!(canSearchExternally.partnerNameIsValid && canSearchExternally.uniqueIdIsValid)) return;
      const {
        partnerRole,
        partnerType: { partnerTypeId },
        partnerGeneralData: { uniqueId, partnerName },
        documentId,
        siteId,
        partnerId
      } = this.props;

      const partnerInformation = { uniqueId, partnerRole, partnerTypeId, partnerId, partnerName };
      return this.props.actions.loadPartnerFromQuery(documentId, siteId, partnerInformation);
    });
  };

  render() {
    const {
      partnerGeneralData,
      partnerRole,
      isAssociatedPartner,
      partnerRoleIdValidationInfo,
      validationInfo,
      checkMandatoryFields,
      onPropertyChange,
      onUniqueIdBlur,
      citizenships,
      partnerTypes,
      idTypes,
      relations,
      rights,
      applicationFeatures,
      config,
      partnerRoles,
      agreesWithPersonalDataUse,
      legalForms,
      t,
      partnerType,
      defaultOCRFileExtensions,
      gdpr,
      genderTypes,
      setIsPartnerFromModal,
      isRestructuring,
      isCession,
      isExternalVerificationInProgress,
      canSearchExternally,
      agreesWithPersonalDataUseReadOnly,
      isAssociatedDocumentPartner,
      partnerRoleDisabled,
      documentId,
      siteId
    } = this.props;

    const { offerForCompanies, enableOcrIntegration } = applicationFeatures;
    const disabledSearchByUniqueId = window.location.pathname.includes(`partner-files/${documentId}/${siteId}`);

    return (
      <>
        <Grid container spacing={2}>
          {isAssociatedPartner && (
            <Grid item xs={12} md={4}>
              <Autocomplete
                id={'offerPartnerFormCard_partnerRole'}
                fullWidth
                required
                isClearable={false}
                value={partnerRole}
                onChange={onPropertyChange('partnerRoleId')}
                error={partnerRoleIdValidationInfo ? true : false}
                helperText={partnerRoleIdValidationInfo}
                label={t('OfferEdit.Client.PartnerRole')}
                valueKey='partnerRoleId'
                labelKey='name'
                options={partnerRoles}
                disabled={
                  rights.readOnlyFull || this.props.waitingForOCR || (!isAssociatedPartner && isRestructuring) || partnerRoleDisabled
                }
              />
            </Grid>
          )}
          {this.props.waitingForOCR && (
            <Grid item xs={12}>
              <Typography>{t('OfferEdit.Client.WaitingForOCR')}</Typography>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <Autocomplete
              id={'offerPartnerFormCard_partnerType'}
              disabled={
                rights.readOnlyFull ||
                !offerForCompanies ||
                partnerGeneralData.partnerExists ||
                (isAssociatedPartner != null && !isAssociatedPartner && !isCession)
              }
              fullWidth
              value={partnerType}
              valueKey='partnerTypeId'
              labelKey='partnerTypeName'
              label={t('OfferEdit.Client.PartnerType')}
              options={partnerTypes}
              onChange={onPropertyChange('partnerType')}
              isClearable={false}
              error={!partnerType}
              helperText={!partnerType ? t('Validations.Generic.Mandatory') : ''}
            />
          </Grid>
        </Grid>
        <Grid container my={1} />
        {partnerType &&
          (partnerType.typeCheck && partnerType.typeCheck.isPF ? (
            <OfferPartnerPFGeneralData
              fileInputRef={this.fileInputRef}
              onFileSelected={this.onFileSelected}
              uploadClick={this.uploadClick}
              partnerGeneralData={partnerGeneralData}
              partnerRoleId={partnerRole.partnerRoleId}
              partnerRoleIdValidationInfo={partnerRoleIdValidationInfo}
              validationInfo={validationInfo}
              checkMandatoryFields={checkMandatoryFields}
              agreesWithPersonalDataUse={agreesWithPersonalDataUse}
              onPropertyChange={onPropertyChange}
              onUniqueIdBlur={onUniqueIdBlur}
              citizenships={citizenships}
              partnerTypes={partnerTypes}
              idTypes={idTypes}
              relations={relations}
              config={config}
              loadGDPR={this.props.actions.loadGDPR}
              pfPartners={this.props.actions.loadPFPartners}
              setIsPartnerFromModal={setIsPartnerFromModal}
              gdpr={gdpr}
              genderTypes={genderTypes}
              defaultOCRFileExtensions={defaultOCRFileExtensions}
              disabled={rights.readOnlyFull || this.props.waitingForOCR}
              loadCountries={this.props.actions.loadCountries}
              loadCounties={this.props.actions.loadCounties}
              loadCities={this.props.actions.loadCities}
              loadCitiesWithCounty={this.props.actions.loadCitiesWithCounty}
              isRestructuring={isRestructuring}
              isAssociatedPartner={isAssociatedPartner}
              agreesWithPersonalDataUseReadOnly={agreesWithPersonalDataUseReadOnly}
              isAssociatedDocumentPartner={isAssociatedDocumentPartner}
              enableOcrIntegration={enableOcrIntegration}
              disabledSearchByUniqueId={disabledSearchByUniqueId}
            />
          ) : (
            <>
              <OfferPartnerPJGeneralData
                partnerGeneralData={partnerGeneralData}
                validationInfo={validationInfo}
                checkMandatoryFields={checkMandatoryFields}
                onPropertyChange={onPropertyChange}
                onUniqueIdBlur={onUniqueIdBlur}
                partnerTypes={partnerTypes}
                legalForms={legalForms}
                loadLegalPartners={this.props.actions.loadLegalPartners}
                disabled={rights.readOnlyFull}
                isRestructuring={isRestructuring}
                isAssociatedPartner={isAssociatedPartner}
                partnerTypeId={partnerType?.partnerTypeId}
                partnerTypeCode={partnerType?.partnerTypeCode}
                isPJ={partnerType?.typeCheck?.isPJ}
                openExternalSearchConfirmationModal={this.toggleExternalSearchConfirmationModal}
                checkLegalPartnerFilters={this.checkLegalPartnerFilters}
                isExternalVerificationInProgress={isExternalVerificationInProgress}
                setIsPartnerFromModal={setIsPartnerFromModal}
                canSearchExternally={canSearchExternally}
                disabledSearchByUniqueId={disabledSearchByUniqueId}
              />

              {this.state.showExternalSearchConfirmationModal && (
                <ExternalSearchConfirmationModal
                  closeExternalSearchConfirmationModal={this.toggleExternalSearchConfirmationModal}
                  getCompanyExternalInformation={() => {
                    this.getCompanyInfo();
                    this.toggleExternalSearchConfirmationModal();
                  }}
                />
              )}
            </>
          ))}
      </>
    );
  }
}

OfferPartnerGeneralDataContainer.propTypes = {
  t: PropTypes.func.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number.isRequired,
  partnerType: PropTypes.object,
  partnerGeneralData: PropTypes.object.isRequired,
  partnerRole: PropTypes.object,
  isAssociatedPartner: PropTypes.bool,
  partnerRoleIdValidationInfo: PropTypes.string,
  validationInfo: PropTypes.object.isRequired,
  checkMandatoryFields: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  agreesWithPersonalDataUse: PropTypes.bool.isRequired,
  citizenships: PropTypes.array.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  legalForms: PropTypes.array.isRequired,
  idTypes: PropTypes.array.isRequired,
  relations: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,
  partnerRoles: PropTypes.array.isRequired,
  genderTypes: PropTypes.array.isRequired,
  defaultOCRFileExtensions: PropTypes.array.isRequired,
  gdpr: PropTypes.object.isRequired,
  rights: PropTypes.object.isRequired,
  applicationFeatures: PropTypes.object.isRequired,
  isRestructuring: PropTypes.bool.isRequired,
  isCession: PropTypes.bool.isRequired,
  documentTypeId: PropTypes.number.isRequired,
  isFacilityFrame: PropTypes.bool,
  isExternalVerificationInProgress: PropTypes.bool,
  canSearchExternally: PropTypes.object.isRequired,
  handleExternalSearch: PropTypes.func.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  waitingForOCR: PropTypes.bool.isRequired,
  agreesWithPersonalDataUseReadOnly: PropTypes.bool.isRequired,
  isAssociatedDocumentPartner: PropTypes.bool,
  partnerRoleDisabled: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  const { isFacilityFrame } = ownProps;
  return {
    citizenships: state.clientData.citizenships,
    partnerTypes: getFilteredPartnerTypes(state, isFacilityFrame),
    legalForms: state.dictionaries.legalForms.isLoaded ? state.dictionaries.legalForms.items : [],
    idTypes: state.clientData.idTypes,
    relations: state.clientData.relations,
    config: state.offerConfig,
    gdpr: state.clientData.gdpr,
    partnerRoles: state.clientData.partnerRoles,
    defaultOCRFileExtensions: state.clientData.defaultOCRFileExtensions,
    genderTypes: state.dictionaries.genderTypes.isLoaded ? state.dictionaries.genderTypes.items : [],
    isRestructuring: getOfferType(state) === OfferType.restructuring.id,
    isCession: getOfferType(state) === OfferType.cession.id,
    documentTypeId: window.location.pathname.includes('partner-files') ? documentTypeIds.PartnerFile : getDocumentType(state),
    isExternalVerificationInProgress: getExternalVerificationStatus(state, ownProps)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCitizenships,
        loadPartnerTypes,
        loadIdTypes,
        loadPartnerRoles,
        loadClientByIdCard,
        loadRelations,
        loadGDPR,
        loadPFPartners,
        loadLegalPartners,
        loadLegalForms,
        loadGenderTypes,
        loadPartnerFromQuery,
        loadCounties,
        loadCountries,
        loadCities,
        loadCitiesWithCounty,
        loadDefaultOCRFileExtensions
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(compose(withRights, withApplicationFeatures)(OfferPartnerGeneralDataContainer));
